import * as React from "react"
import * as ReactDOM from "react-dom"
import 'react-dates/initialize';
import { DateRangePicker, DateRangePickerShape, FocusedInputShape, OrientationShape } from 'react-dates';
import * as moment_base from 'moment';
import { Moment } from 'moment';
import { extendMoment, DateRange } from 'moment-range';
import * as Immutable from 'immutable'

import { default as PeriodPicker, StartEnd, DisabledProps, PrefixProps } from './PeriodPicker'
import 'whatwg-fetch';

import 'react-dates/lib/css/_datepicker.css';

import * as PeriodEventEmitter from './PeriodEventEmitter'
import IsSameMoment from '../IsSameMoment'

import PricingError from '../../components/PricingError'

const moment = extendMoment(moment_base);
const now = Date.now()

declare var I18n : { locale: string, t: (_:string) => string }

const blockedTypes = Immutable.Set<string>(["unavailable", "reserved", "booked"])
type Availability = {
  start_date: Moment,
  end_date: Moment,
  compact_range: DateRange,
  range_for_start_date: DateRange,
  range_for_end_date: DateRange,
  period_type: "available" | "unavailable" | "lastminute" | "reserved" | "booked"
}
type Availabilities = Immutable.List<Availability>

type PricingPeriod = {
  start_date: Moment,
  end_date: Moment,
  range: DateRange,
  check_in_out_day: 0 | 1 | 2 | 3 | 4 | 5 | 6 | null,
  min_days: number
}
type PricingPeriods = Immutable.List<PricingPeriod>
type PickerData = { availabilities: Availabilities, pricing_periods: PricingPeriods }

export type DataUrlProps = { data_url: string }

type Props = StartEnd & PrefixProps & DataUrlProps & DisabledProps & {
  onDatesChange: (range: StartEnd) => void
  onFocusChange: (fi: FocusedInputShape) => void
  focusedInput: FocusedInputShape
  showClearDate?: boolean
  orientation?: OrientationShape
  numberOfMonths?: number
  close: () => void
}

type State = PickerData

export default class ResourcePeriodPicker extends React.Component<Props, State> {

  constructor(props, context) {
    super(props, context)
    this.state = {
      availabilities: Immutable.List<Availability>(),
      pricing_periods: Immutable.List<PricingPeriod>(),
    }
  }

  componentWillMount() {
    this.refreshPeriods();
  }

  componentDidUpdate(prevProps : Props, prevState : State) {
    if (this.props.data_url != prevProps.data_url) {
      this.refreshPeriods();
    }
    if (this.isPeriodChanged(prevProps) && this.props.focusedInput === null) {
      PeriodEventEmitter.publish({ start_date: this.props.startDate, end_date: this.props.endDate, source: this.props.prefix, focusedInput: this.props.focusedInput})
    }
  }

  isPeriodChanged(prevProps: Props) : boolean {
    return prevProps.focusedInput !== this.props.focusedInput || !IsSameMoment(prevProps.startDate, this.props.startDate) || !IsSameMoment(prevProps.endDate, this.props.endDate)
  }

  async refreshPeriods() {
    if (this.props.data_url === null) {
      this.setState({...this.state, availabilities: Immutable.List<Availability>(), pricing_periods: Immutable.List<PricingPeriod>()})
      return
    }

    const data = await this.fetchPeriods()
    this.setState({...this.state, availabilities: data.availabilities, pricing_periods: data.pricing_periods})
  }

  async fetchPeriods():Promise<PickerData> {
    const response = await fetch(this.props.data_url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: [
        ['Accept', 'application/json'],
        ['Content-Type', 'application/json']
      ]
    })

    const pd : PickerData = await response.json()

    const avs:Availabilities = pd.availabilities.reduce(
      (acc, av) => {
        let s = moment(av.start_date).endOf('day')
        let e = moment(av.end_date).startOf('day')
        return acc.push( {
          ...av,
          start_date: s,
          end_date: e,
          compact_range: moment.range(s.clone().endOf('day'), e.clone().startOf('day')),
          range_for_start_date: moment.range(s.clone().startOf('day'), e.clone().startOf('day')),
          range_for_end_date: moment.range(s.clone().endOf('day'), e.clone().endOf('day')),
        })
      }, Immutable.List<Availability>()
    )
    const prs:PricingPeriods = pd.pricing_periods.reduce(
      (acc, pr) => {
        let s = moment(pr.start_date).startOf('day');
        let e = moment(pr.end_date).endOf('day');
        return acc.push( {
          ...pr,
          check_in_out_day: isNaN(pr.check_in_out_day) ? null : pr.check_in_out_day,
          start_date: s,
          end_date: e,
          range: moment.range(s, e)}
        )
      },
      Immutable.List<PricingPeriod>()
    )
    return { availabilities: avs, pricing_periods: prs }
  }

  onDatesChange(range: StartEnd) {
    let newDay = range.startDate == this.props.startDate ? { target: 'endDate', day: range.endDate } : { target: 'startDate', day: range.startDate };

    // Ensure we're not setting an invalid date range when changing an existing selected range
    if (this.isInSelectedRange(newDay.day) && this.isUnpickable(newDay.day)) {
      range[newDay.target] = this.nearestValidDay(newDay.day)
      if (range.startDate && range.startDate.isSame(range.endDate, 'day')) range.endDate = null // Clear `endDate` if it's the same day as `startDate`
    }

    return this.props.onDatesChange(range);
  }

  nearestValidDay(day:Moment) {
    let validDay = undefined
    let candidates = Immutable.List([day.clone(), day.clone()])
    while (validDay === undefined && (candidates.last().isAfter(now) || candidates.first().isBefore(moment().add(2, 'years')))) {
      candidates.first().add(1, 'day')
      candidates.last().subtract(1, 'day')
      validDay = candidates.find(d => !this.isUnpickable(d))
    }
    return validDay
  }

  blockedAvailabilities() : Availabilities {
    return this.state.availabilities.filter((av) => blockedTypes.contains(av.period_type)).toList();
  }

  // This method is called pretty often, but it should be pure within the lifetime of the component
  // To prevent repeated evaluation of availabilities, memoization is applied
  isDayBlocked(day:Moment) {
    let key = `${this.props.data_url}-${day.valueOf()}`;
    day.hours(12);
    return this.pricingPeriodForDay(day) === undefined || this.isDayUnavailableAsStartAndEndDate(day)
  }

  isDayUnavailableAsStartAndEndDate(day : Moment) : boolean {
    return this.blockedAvailabilities().some((av) => {
      return av.compact_range.contains(day)
    })
  }

  isDayUnavailableAsStartDate(day : Moment) : boolean {
    return this.blockedAvailabilities().some((av) => {
      return av.range_for_start_date.contains(day)
    })
  }

  isDayUnavailableAsEndDate(day : Moment) : boolean {
    return this.blockedAvailabilities().some((av) => {
      return av.range_for_end_date.contains(day)
    })
  }

  // This method is called pretty often, but it should be pure within the lifetime of the component
  // To prevent repeated lookup of pricing_periods, memoization is applied
  pricingPeriodForDay(day:Moment) {
    if (this.state.pricing_periods.isEmpty()) return undefined;
    let key = `${this.props.data_url}-${day.valueOf()}`;
    return this.state.pricing_periods.find(pr => pr.range.contains(day))
  }

  // This method is used for both styling and functionality.
  // Once a selection is made we want the styling indicate as such, even though some dates should not be selectable.
  // The `isInSelectedRange` line of this method ensures proper display.
  // In `onDatesChange` we make sure an invalid date is picked.
  isOutsideRange(day:Moment) {
    if (this.isInSelectedRange(day)) return false; //  Don't display as unselectable if the day is already part of a selection
    return this.isUnpickable(day);
  }

  isInSelectedRange(day:Moment) {
    return day !== null
        && this.props.startDate !== null
        && this.props.endDate   !== null
        && moment.range(this.props.startDate, this.props.endDate).contains(day);
  }
  isUnpickable(day:Moment) {
    if (day.isBefore(now)) return true;                                   // Dates before today are always out of range
    if (this.isDayBlocked(day)) return false;                             // Don't grey out days that are blocked

    let startDate = this.props.startDate;

    let periodForDay = this.pricingPeriodForDay(day);
    if (periodForDay == undefined || (periodForDay.check_in_out_day !== null &&                         // If a check_in_out_day has been set for this day's period
        periodForDay.check_in_out_day !== day.day())) return true;         // grey out days that don't correspond to the set day

    if (startDate === null || this.props.focusedInput === 'startDate') {
      if (this.isDayUnavailableAsStartDate(day)) {
        return true;
      }

      let validCheckout = day.clone().add(periodForDay.min_days - 1, 'days')
      let days_to_checkout = Immutable.List<Moment>(moment.range(day, validCheckout).by('day'));
      return days_to_checkout.some(d => this.pricingPeriodForDay(d) === undefined || this.isDayBlocked(d)) // Grey out dates that would prevent user from selecting enough days
    } else {
      if (startDate.isSame(day, 'day')) return false;                    // Don't grey out selected start date

      let days_from_start = Immutable.List<Moment>(moment.range(startDate.clone().add(1, 'day'), day).by('day')) // Iterable of days after selected start date until tested date

      // The period between start and tested day could be covered by multiple PricingPeriods
      // The following determines the lowest defined "minimum days", as per RentalSumCalculator#min_days
      let min_days = days_from_start.reduce( (acc, d) => this.pricingPeriodForDay(d) ? Math.min(acc, this.pricingPeriodForDay(d).min_days) : acc, periodForDay.min_days ) // Seeded with min_days for tested date

      let validCheckout = startDate.clone().add((min_days - 1), 'days');
      if (day.isBefore(validCheckout, 'day')) return true;                // Grey out days before valid checkout date

      return days_from_start.some(d => this.isDayUnavailableAsEndDate(d))              // Grey out this day if any day between selected start date and it is blocked
    }
  }

  renderDay(day: Moment) {
    let arrive = this.blockedAvailabilities().some(a => a.start_date.isSame(day, 'day'))
    let leave  = this.blockedAvailabilities().some(a => a.end_date.isSame(day, 'day'))
    let day_class = 'CalendarDay_button__contents'
    if (arrive && !leave) day_class += ' CalendarDay_button__contents--arrive'
    if (!arrive && leave) day_class += ' CalendarDay_button__contents--leave'
    if (arrive && leave)  day_class += ' CalendarDay_button__contents--leave_and_arrive'
    if (day.isBefore(now)) day_class += ' CalendarDay_button__contents--past'
    if (this.isDayBlocked(day)) day_class += ' CalendarDay_button__contents--blocked'
    if (this.isOutsideRange(day)) day_class += ' CalendarDay_button__contents--outside-range'
    return <div className={day_class}>{day.date()}</div>
  }

  hasPriceInRange(range: DateRange) : boolean {
    return this.state.pricing_periods.some(x => x.range.overlaps(range))
  }

  renderCalenderInfo(range: DateRange) {
    if(this.hasPriceInRange(range) === false) {
      return <div className="DateRangePickerNotice">
        <PricingError
          error={I18n.t('availabilities.no_pricing_for_period')}
          close={() => this.props.close()}
        />
      </div>
    } else {
      return null;
    }
  }

  render() {
    return (
      <div>
        <PeriodPicker
          {...this.props}
          renderDay={(day: Moment) => this.renderDay(day)}
          onDatesChange={(range: StartEnd) => this.onDatesChange(range)}
          isDayBlocked={(day: Moment) => this.isDayBlocked(day)}
          isOutsideRange={(day: Moment) => this.isOutsideRange(day)}
          renderCalenderInfo={(range: DateRange) => this.renderCalenderInfo(range)}
        />
      </div>
    )
  }
}